import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";
import {
    RefreshCw, Search, Eye, Plus, List, Grid, Filter, X,
    IndianRupee, ArrowLeft, Package, User, Calendar, Pill
} from 'lucide-react';
import Pagination from '../../components/Pagination';
import { MdCheckCircle } from 'react-icons/md';

const PurchaseReturn = () => {
    const navigate = useNavigate();
    const [returnsList, setReturnsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [viewMode, setViewMode] = useState('list');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    // Modals
    const [showViewModal, setShowViewModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [selectedForStatus, setSelectedForStatus] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setViewMode(window.innerWidth < 640 ? 'grid' : 'list');
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchReturns = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/purchase-returns');
            const returnsData = Array.isArray(res.data?.purchaseReturns) ? res.data.purchaseReturns : [];
            setReturnsList(returnsData);
            setError('');
        } catch (err) {
            console.error('Error fetching purchase returns', err);
            setError('Failed to load purchase returns.');
            setReturnsList([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReturns();
    }, []);

    // Filtering
    const filtered = returnsList.filter((r) => {
        const matchesSearch =
            r.id.toString().includes(searchTerm) ||
            (r.return_reason || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.notes || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.supplier?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.supplier?.mobile_number || '').includes(searchTerm) ||
            r.returnitems?.some(item =>
                (item.medicine?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
            );

        const matchesStatus = !statusFilter || r.return_status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Add this helper function inside the component (before return)
    const isStatusFinal = (status) => {
        return ['APPROVED', 'REJECTED', 'PROCESSED', 'REFUNDED'].includes(status);
    };

    const totalItems = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / limit));
    const startIndex = (page - 1) * limit;
    const endIndex = page * limit;
    const visible = filtered.slice(startIndex, endIndex);

    useEffect(() => setPage(1), [searchTerm, statusFilter]);
    useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

    const getStatusBadge = (status) => {
        const config = {
            PENDING: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
            APPROVED: 'bg-green-100 text-green-800 border border-green-200',
            REJECTED: 'bg-red-100 text-red-800 border border-red-200',
            PROCESSED: 'bg-blue-100 text-blue-800 border border-blue-200',
            REFUNDED: 'bg-teal-100 text-teal-800 border border-teal-200'
        };
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${config[status] || config.PENDING}`}>
                {status}
            </span>
        );
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setPage(1);
    };

    // View Modal
    const openViewModal = async (id) => {
        setModalLoading(true);
        setShowViewModal(true);
        try {
            const res = await axiosInstance.get(`/purchase-returns/${id}`);
            setSelectedReturn(res.data);
        } catch (err) {
            console.error("Failed to load return details", err);
            setSelectedReturn(null);
        } finally {
            setModalLoading(false);
        }
    };

    const closeViewModal = () => {
        setShowViewModal(false);
        setSelectedReturn(null);
    };

    // Status Modal
    const openStatusModal = (item) => {
        setSelectedForStatus(item);
        setShowStatusModal(true);
    };

    const closeStatusModal = () => {
        setShowStatusModal(false);
        setSelectedForStatus(null);
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center space-x-3">
                                <div className="bg-teal-600 p-3 rounded-xl shadow-md">
                                    <RefreshCw className="h-8 w-8 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Purchase Returns</h1>
                                    <p className="text-sm text-gray-600">Manage and track all medicine returns</p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/new-purchase-return')}
                                className="flex items-center justify-center px-5 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-md hover:shadow-xl transition-all duration-200"
                            >
                                <Plus size={20} className="mr-2" />
                                <span className="">Add Return</span>
                            </button>
                        </div>

                        {/* Search & Filters */}
                        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                            <div className="flex flex-col gap-4">

                                {/* FIRST ROW */}
                                <div className="flex flex-col md:flex-row gap-4 items-center">

                                    {/* Search */}
                                    <div className="relative flex-1 w-full">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Search by ID, reason, supplier, medicine..."
                                            className="pl-10 w-full py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600 transition-all"
                                        />
                                    </div>

                                    {/* Buttons */}
                                    <div className="flex gap-2">
                                        <select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            className="w-full md:w-auto px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 focus:border-teal-600"
                                        >
                                            <option value="">All Status</option>
                                            <option value="PENDING">Pending</option>
                                            <option value="APPROVED">Approved</option>
                                            <option value="REJECTED">Rejected</option>
                                            <option value="PROCESSED">Processed</option>
                                            <option value="REFUNDED">Refunded</option>
                                        </select>
                                        <button
                                            onClick={fetchReturns}
                                            className="flex items-center gap-2 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                                        >
                                            <RefreshCw className="w-4 h-4" />
                                            <span className="hidden md:inline">Refresh</span>
                                        </button>

                                        <button
                                            onClick={handleClearFilters}
                                            className="flex items-center gap-2 px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-all"
                                        >
                                            <Filter className="w-4 h-4" />
                                            <span className="hidden md:inline">Clear</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
                            <span className="font-medium">Error:</span> {error}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center items-center py-16">
                            <RefreshCw className="w-10 h-10 text-teal-600 animate-spin" />
                        </div>
                    ) : visible.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-md p-12 text-center">
                            <div className="w-20 h-20 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                                <RefreshCw className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-800 mb-2">No returns found</h3>
                            <button
                                onClick={() => navigate('/new-purchase-return')}
                                className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all"
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Add Return
                            </button>
                        </div>
                    ) : viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {visible.map((item) => (
                                <div key={item.id} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100">
                                    <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-5 text-white">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-lg font-bold">#{item.id}</p>
                                                <p className="text-xs opacity-90">{new Date(item.return_date).toLocaleDateString("en-GB")}</p>
                                            </div>
                                            {getStatusBadge(item.return_status)}
                                        </div>
                                    </div>
                                    <div className="p-5 space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                                                <RefreshCw className="w-5 h-5 text-teal-600" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500">Supplier</p>
                                                <p className="font-semibold text-gray-800">{item.supplier.name}</p>
                                            </div>
                                        </div>
                                        {item.return_reason && (
                                            <p className="text-sm text-gray-600 italic">"{item.return_reason}"</p>
                                        )}
                                        <div className="pt-3 border-t">
                                            <p className="text-xs text-gray-500">Return Amount</p>
                                            <p className="text-lg font-bold text-teal-600">₹{parseFloat(item.total_return_amt).toFixed(2)}</p>
                                        </div>
                                        <div className="flex gap-3 pt-4">
                                            <button onClick={() => openViewModal(item.id)} className="w-1/2 py-2.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all duration-200 font-semibold">
                                                <Eye className="inline-block mr-2 w-5 h-5" /> View
                                            </button>
                                            <button onClick={() => openStatusModal(item)} className="w-1/2 py-2.5 bg-teal-50 text-teal-600 rounded-lg hover:bg-teal-100 transition-all duration-200 font-semibold" title="Change Status">
                                                <RefreshCw className="inline-block mr-2 w-5 h-5" /> Status
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow-lg border border-teal-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                                        <tr>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Return ID</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Supplier</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Reason</th>
                                            <th className="px-6 py-4 text-left font-semibold uppercase tracking-wider">Status</th>
                                            <th className="px-6 py-4 text-right font-semibold uppercase tracking-wider">Amount</th>
                                            <th className="px-6 py-4 text-center font-semibold uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {visible.map((item) => (
                                            <tr key={item.id} className="hover:bg-teal-50 transition-all duration-200">
                                                <td className="px-6 py-4 font-medium text-gray-900">#{item.id}</td>
                                                <td className="px-6 py-4 text-gray-700">{new Date(item.return_date).toLocaleDateString('en-GB')}</td>
                                                <td className="px-6 py-4">
                                                    <div>
                                                        <p className="font-medium text-gray-900">{item.supplier.name}</p>
                                                        <p className="text-xs text-gray-500">{item.supplier.mobile_number}</p>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-gray-600 italic max-w-xs truncate">{item.return_reason || '-'}</td>
                                                <td className="px-6 py-4">{getStatusBadge(item.return_status)}</td>
                                                <td className="px-6 py-4 text-right font-semibold text-teal-600">₹{parseFloat(item.total_return_amt).toFixed(2)}</td>
                                                {/* <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button onClick={() => openViewModal(item.id)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg">
                                                            <Eye size={20} />
                                                        </button>
                                                        <Link to={`/purchase-returns/${item.id}/status`} className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg" title="Change Status">
                                                            <RefreshCw size={20} />
                                                        </Link>
                                                    </div>
                                                </td> */}
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center gap-2">
                                                        <button onClick={() => openViewModal(item.id)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg">
                                                            <Eye size={20} />
                                                        </button>

                                                        {/* Conditional Status Button */}
                                                        {!isStatusFinal(item.return_status) && (
                                                            <Link to={`/purchase-returns/${item.id}/status`} className="p-2 text-teal-600 hover:bg-teal-100 rounded-lg" title="Change Status">
                                                                <MdCheckCircle size={20} />
                                                            </Link>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-600">
                                <div>Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} returns</div>
                                <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />
                            </div>
                        </div>
                    )}

                    {/* View Modal */}
                    {showViewModal && (
                        <PurchaseReturnViewModal
                            returnData={selectedReturn}
                            loading={modalLoading}
                            onClose={closeViewModal}
                        />
                    )}
                </div>
            </div>
        </Authenticated>
    );
};

/* ======================================================
   PURCHASE RETURN VIEW MODAL (MedicineView Style)
   ====================================================== */
const PurchaseReturnViewModal = ({ returnData, loading, onClose }) => {
    const InfoCard = ({ icon: Icon, label, value, highlight = false, className = '' }) => (
        <div className={`p-3 sm:p-4 rounded-xl border transition-all hover:shadow-md ${highlight ? className || 'bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200' : 'bg-white border-gray-200'}`}>
            <div className="flex items-start gap-2 sm:gap-3">
                <div className={`p-1.5 sm:p-2 rounded-lg ${highlight ? (className.includes('blue') ? 'bg-blue-100' : className.includes('purple') ? 'bg-purple-100' : className.includes('orange') ? 'bg-orange-100' : 'bg-teal-100') : 'bg-gray-100'}`}>
                    <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${highlight ? (className.includes('blue') ? 'text-blue-600' : className.includes('purple') ? 'text-purple-600' : className.includes('orange') ? 'text-orange-600' : 'text-teal-600') : 'text-gray-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${highlight ? (className.includes('blue') ? 'text-blue-500' : className.includes('purple') ? 'text-purple-500' : className.includes('orange') ? 'text-orange-500' : 'text-teal-500') : 'text-gray-500'}`}>{label}</p>
                    <p className="text-sm font-semibold text-gray-900 break-words">{value || '-'}</p>
                </div>
            </div>
        </div>
    );

    const Badge = ({ children, color = "gray" }) => {
        const colors = {
            teal: "bg-teal-100 text-teal-700 border-teal-200",
            red: "bg-red-100 text-red-700 border-red-200",
            blue: "bg-blue-100 text-blue-700 border-blue-200",
            yellow: "bg-yellow-100 text-yellow-700 border-yellow-200",
            green: "bg-green-100 text-green-700 border-green-200",
            purple: "bg-purple-100 text-purple-700 border-purple-200"
        };
        return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${colors[color]}`}>{children}</span>;
    };

    const getStatusColor = (status) => {
        const map = { PENDING: "yellow", APPROVED: "green", REJECTED: "red", PROCESSED: "blue", REFUNDED: "teal" };
        return map[status] || "gray";
    };

    if (loading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                <div className="bg-white rounded-2xl w-full max-w-5xl p-6">
                    <div className="animate-pulse space-y-6">
                        <div className="flex items-center gap-4">
                            <div className="h-12 w-12 bg-teal-200 rounded-2xl"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
                                <div className="h-4 bg-gray-100 rounded w-1/4"></div>
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                            {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-gray-100 rounded-xl"></div>)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!returnData) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
                <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 text-red-500">X</div>
                    <p className="text-red-600 font-medium">Failed to load return details.</p>
                    <button onClick={onClose} className="mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg">Close</button>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4 overflow-y-auto">
            <div className="bg-gray-50 rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-2xl">
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Return Details - #{returnData.id}</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
                </div>

                <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-start gap-4">
                        <button onClick={onClose} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group text-sm">
                            <div className="p-1 rounded-lg group-hover:bg-gray-100"><ArrowLeft className="w-4 h-4" /></div>
                            <span className="font-medium">Back</span>
                        </button>
                        <div className="flex items-start gap-3 sm:gap-4 flex-1">
                            <div className="p-2.5 sm:p-3 bg-teal-600 rounded-2xl shadow-lg">
                                <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <div className="flex-1">
                                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1">Return #{returnData.id}</h1>
                                <p className="text-sm sm:text-base text-gray-600 font-medium">
                                    {new Date(returnData.return_date).toLocaleDateString("en-GB")}
                                </p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    <Badge color={getStatusColor(returnData.return_status)}>{returnData.return_status}</Badge>
                                    {returnData.refund_method && (
                                        <Badge color="purple">
                                            <IndianRupee className="w-3 h-3 mr-1" />
                                            {returnData.refund_method.replace('_', ' ')}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Quick Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <InfoCard icon={User} label="Supplier" value={returnData.supplier?.name} highlight className="from-teal-50 to-cyan-50 border-teal-200" />
                        <InfoCard icon={Package} label="Items" value={returnData.returnitems?.length || 0} highlight className="from-blue-50 to-indigo-50 border-blue-200" />
                        <InfoCard icon={IndianRupee} label="Total Amount" value={`₹${parseFloat(returnData.total_return_amt).toFixed(2)}`} highlight className="from-purple-50 to-pink-50 border-purple-200" />
                        <InfoCard icon={Calendar} label="Refund Date" value={returnData.refund_date ? new Date(returnData.refund_date).toLocaleDateString("en-GB") : '-'} highlight className="from-orange-50 to-amber-50 border-orange-200" />
                    </div>

                    {/* Details */}
                    <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
                        <div className="bg-teal-600 px-4 sm:px-6 py-3 sm:py-4">
                            <h2 className="text-base sm:text-lg font-bold text-white">Return Details</h2>
                        </div>
                        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                            {[
                                { label: "Return Reason", value: returnData.return_reason || '-' },
                                { label: "Notes", value: returnData.notes || '-', multi: true },
                                { label: "Refund Amount", value: returnData.refund_amount ? `₹${parseFloat(returnData.refund_amount).toFixed(2)}` : '-' },
                                { label: "Refund Method", value: returnData.refund_method ? returnData.refund_method.replace('_', ' ') : '-' },
                                { label: "Status", value: returnData.return_status, badge: getStatusColor(returnData.return_status) },
                            ].map((item, i) => (
                                <div key={i} className="space-y-1">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                                    {item.badge ? (
                                        <Badge color={item.badge}>{item.value}</Badge>
                                    ) : (
                                        <p className={`text-gray-900 font-medium ${item.multi ? 'whitespace-pre-wrap' : ''}`}>{item.value}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Returned Items */}
                    {returnData.returnitems?.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-sm border-2 border-gray-100 overflow-hidden">
                            {/* Header - Common for both */}
                            <div className="bg-teal-600 px-4 py-3 sm:py-4">
                                <h2 className="text-base sm:text-lg font-bold text-white">
                                    Returned Items ({returnData.returnitems.length})
                                </h2>
                            </div>

                            {/* === MOBILE: CARD VIEW (Hidden on Desktop) === */}
                            <div className="block lg:hidden p-4 space-y-3">
                                {returnData.returnitems.map((item, i) => (
                                    <div
                                        key={i}
                                        className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:bg-teal-50 transition-all duration-200"
                                    >
                                        {/* Medicine Name */}
                                        <div className="mb-2">
                                            <div className="font-bold text-gray-900 text-sm">
                                                {item.medicine?.name || 'Unknown'}
                                            </div>
                                            {item.medicine?.generic_name && (
                                                <div className="text-xs text-gray-500">{item.medicine.generic_name}</div>
                                            )}
                                        </div>

                                        {/* Responsive 2-column grid */}
                                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase">Batch</div>
                                                <div className="font-medium">{item.batch_number}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-gray-500 uppercase">Qty</div>
                                                <div className="font-bold text-teal-600">{item.return_quantity}</div>
                                            </div>
                                            <div>
                                                <div className="text-xs text-gray-500 uppercase">Rate</div>
                                                <div className="font-medium">₹{parseFloat(item.original_price_per_package).toFixed(2)}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-xs text-gray-500 uppercase">Amount</div>
                                                <div className="font-bold text-teal-600">₹{parseFloat(item.return_value).toFixed(2)}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* === DESKTOP: TABLE VIEW (Hidden on Mobile) === */}
                            <div className="hidden lg:block overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-green-100 border-b">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Medicine</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Batch</th>
                                            <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Qty</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Rate</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {returnData.returnitems.map((item, i) => (
                                            <tr key={i} className="hover:bg-teal-50 transition-colors">
                                                <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                                                    <div>
                                                        <div className="font-semibold">{item.medicine?.name || 'Unknown'}</div>
                                                        {item.medicine?.generic_name && (
                                                            <div className="text-xs text-gray-500">{item.medicine.generic_name}</div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">{item.batch_number}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 text-center font-medium">{item.return_quantity}</td>
                                                <td className="px-4 py-3 text-sm text-gray-700 text-right">₹{parseFloat(item.original_price_per_package).toFixed(2)}</td>
                                                <td className="px-4 py-3 text-sm font-semibold text-teal-600 text-right">₹{parseFloat(item.return_value).toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={onClose}
                            className="w-full sm:w-auto px-6 sm:px-8 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm sm:text-base font-medium"
                        >
                            Close
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default PurchaseReturn;